import { LibreBotConfig, Message, ChatResponse, BookingConfig, TimeSlot, BookingResult, Source } from './types';
import { getStyles } from './styles';
import { chatIcon, closeIcon, sendIcon, backIcon, checkIcon } from './icons';
import {
  SupportedLang,
  WidgetTranslations,
  WIDGET_TRANSLATIONS,
  RTL_LANGUAGES,
  detectLanguage
} from './translations';

type WidgetMode = 'chat' | 'booking';

export class LibreBotWidget {
  private config!: Required<LibreBotConfig>;
  private container: HTMLDivElement | null = null;
  private modal: HTMLDivElement | null = null;
  private messagesContainer: HTMLDivElement | null = null;
  private input: HTMLInputElement | null = null;
  private isOpen = false;
  private messages: Message[] = [];
  private isLoading = false;
  private sessionId: string | null = null;
  private translations: WidgetTranslations = WIDGET_TRANSLATIONS.en;
  private isRtl = false;

  // Booking mode state
  private mode: WidgetMode = 'chat';
  private bookingConfig: BookingConfig | null = null;
  private selectedDate: string | null = null;
  private selectedSlot: TimeSlot | null = null;
  private availableSlots: TimeSlot[] = [];
  private bookingContainer: HTMLDivElement | null = null;
  private chatContainer: HTMLDivElement | null = null;
  private inputArea: HTMLDivElement | null = null;

  // Track which config options were explicitly provided by user
  private userProvidedConfig: Set<string> = new Set();

  private defaultConfig: Omit<Required<LibreBotConfig>, 'apiKey'> = {
    apiUrl: 'https://librebot.io/api',
    position: 'bottom-right',
    theme: 'dark',
    primaryColor: '#14b8a6',
    title: '', // Will be set from translations
    subtitle: '', // Will be set from translations
    placeholder: '', // Will be set from translations
    welcomeMessage: '', // Will be set from translations
    streaming: true,
    whiteLabel: false,
    showSources: false,
    autoLoadConfig: true,
    lang: 'en',
  };

  constructor(config: LibreBotConfig) {
    if (!config.apiKey) {
      console.error('LibreBot: API key is required');
      return;
    }

    // Track which config options were explicitly provided by user
    // This is used to determine whether remote config should override
    this.userProvidedConfig = new Set(Object.keys(config));

    // Detect language from config or host page
    const detectedLang = config.lang || detectLanguage();
    this.translations = WIDGET_TRANSLATIONS[detectedLang] || WIDGET_TRANSLATIONS.en;
    this.isRtl = RTL_LANGUAGES.includes(detectedLang);

    // Apply defaults with translations (only if user didn't provide them)
    const configWithTranslations: Partial<LibreBotConfig> = {
      ...config,
      lang: detectedLang,
      title: config.title || this.translations.title,
      subtitle: config.subtitle || this.translations.subtitle,
      placeholder: config.placeholder || this.translations.placeholder,
      welcomeMessage: config.welcomeMessage || this.translations.welcomeMessage,
    };

    this.config = { ...this.defaultConfig, ...configWithTranslations } as Required<LibreBotConfig>;

    // Auto-load config from server if enabled
    if (this.config.autoLoadConfig) {
      this.loadRemoteConfig().then(() => this.init());
    } else {
      this.init();
    }
  }

  private getApiPath(endpoint: string): string {
    const isApiSubdomain = this.config.apiUrl.includes('api.librebot.io');
    return isApiSubdomain ? `/${endpoint}` : `/api/${endpoint}`;
  }

  private async loadRemoteConfig(): Promise<void> {
    try {
      const response = await fetch(`${this.config.apiUrl}${this.getApiPath('widget-config')}?key=${this.config.apiKey}`);
      if (response.ok) {
        const remoteConfig = await response.json();
        // Only apply remote config values if the user didn't explicitly provide them
        if (remoteConfig.title && !this.userProvidedConfig.has('title')) {
          this.config.title = remoteConfig.title;
        }
        if (remoteConfig.subtitle && !this.userProvidedConfig.has('subtitle')) {
          this.config.subtitle = remoteConfig.subtitle;
        }
        if (remoteConfig.placeholder && !this.userProvidedConfig.has('placeholder')) {
          this.config.placeholder = remoteConfig.placeholder;
        }
        if (remoteConfig.welcomeMessage && !this.userProvidedConfig.has('welcomeMessage')) {
          this.config.welcomeMessage = remoteConfig.welcomeMessage;
        }
        if (remoteConfig.primaryColor && !this.userProvidedConfig.has('primaryColor')) {
          this.config.primaryColor = remoteConfig.primaryColor;
        }
        if (remoteConfig.position && !this.userProvidedConfig.has('position')) {
          this.config.position = remoteConfig.position;
        }
        if (remoteConfig.theme && !this.userProvidedConfig.has('theme')) {
          this.config.theme = remoteConfig.theme;
        }
        if (remoteConfig.whiteLabel !== undefined && !this.userProvidedConfig.has('whiteLabel')) {
          this.config.whiteLabel = remoteConfig.whiteLabel;
        }
        if (remoteConfig.showSources !== undefined && !this.userProvidedConfig.has('showSources')) {
          this.config.showSources = remoteConfig.showSources;
        }
        if (remoteConfig.streaming !== undefined && !this.userProvidedConfig.has('streaming')) {
          this.config.streaming = remoteConfig.streaming;
        }
      }
    } catch (error) {
      console.warn('LibreBot: Could not load remote config, using defaults', error);
    }
  }

  private init(): void {
    // Load sessionId from localStorage for persistent memory
    this.loadSession();

    // Inject styles
    this.injectStyles();

    // Create widget container
    this.createWidget();

    // Add welcome message
    if (this.config.welcomeMessage) {
      this.addMessage('assistant', this.config.welcomeMessage);
    }
  }

  private getSessionKey(): string {
    return `librebot_session_${this.config.apiKey}`;
  }

  private loadSession(): void {
    try {
      const stored = localStorage.getItem(this.getSessionKey());
      if (stored) {
        this.sessionId = stored;
      }
    } catch {
      // localStorage may not be available
    }
  }

  private saveSession(sessionId: string): void {
    try {
      this.sessionId = sessionId;
      localStorage.setItem(this.getSessionKey(), sessionId);
    } catch {
      // localStorage may not be available
    }
  }

  private injectStyles(): void {
    const styleId = 'librebot-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = getStyles(this.config.primaryColor);
    document.head.appendChild(style);
  }

  private createWidget(): void {
    // Create container
    this.container = document.createElement('div');
    let className = 'librebot-widget';
    if (this.config.theme === 'light') className += ' light';
    if (this.isRtl) className += ' rtl';
    this.container.className = className;

    // Create FAB button
    const fab = document.createElement('button');
    fab.className = `librebot-fab ${this.config.position === 'bottom-left' ? 'left' : ''}`;
    fab.innerHTML = chatIcon;
    fab.onclick = () => this.toggle();

    // Create modal
    this.modal = document.createElement('div');
    this.modal.className = `librebot-modal ${this.config.position === 'bottom-left' ? 'left' : ''}`;
    this.modal.innerHTML = this.getModalHTML();

    // Add to container
    this.container.appendChild(fab);
    this.container.appendChild(this.modal);
    document.body.appendChild(this.container);

    // Get references
    this.messagesContainer = this.modal.querySelector('.librebot-messages');
    this.input = this.modal.querySelector('.librebot-input');
    this.chatContainer = this.modal.querySelector('.librebot-chat-container');
    this.bookingContainer = this.modal.querySelector('.librebot-booking-container');
    this.inputArea = this.modal.querySelector('.librebot-input-area');

    // Add event listeners
    const closeBtn = this.modal.querySelector('.librebot-close');
    closeBtn?.addEventListener('click', () => this.close());

    const sendBtn = this.modal.querySelector('.librebot-send');
    sendBtn?.addEventListener('click', () => this.sendMessage());

    this.input?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    });

    // Auto theme detection
    if (this.config.theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
      this.updateTheme(mediaQuery.matches);
      mediaQuery.addEventListener('change', (e) => this.updateTheme(e.matches));
    }
  }

  private getModalHTML(): string {
    const poweredByHtml = this.config.whiteLabel
      ? ''
      : `<div class="librebot-powered">
        ${this.translations.poweredBy} <a href="https://librebot.io" target="_blank">Libre Bot</a>
      </div>`;

    return `
      <div class="librebot-header">
        <div class="librebot-header-content">
          <div class="librebot-avatar">${chatIcon}</div>
          <div>
            <h3 class="librebot-title">${this.config.title}</h3>
            <p class="librebot-subtitle">${this.config.subtitle}</p>
          </div>
        </div>
        <button class="librebot-close">${closeIcon}</button>
      </div>
      <div class="librebot-chat-container">
        <div class="librebot-messages"></div>
      </div>
      <div class="librebot-booking-container" style="display: none;"></div>
      <div class="librebot-input-area">
        <input type="text" class="librebot-input" placeholder="${this.config.placeholder}" />
        <button class="librebot-send">${sendIcon}</button>
      </div>
      ${poweredByHtml}
    `;
  }

  private updateTheme(isLight: boolean): void {
    if (isLight) {
      this.container?.classList.add('light');
    } else {
      this.container?.classList.remove('light');
    }
  }

  public toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  public open(): void {
    this.isOpen = true;
    this.modal?.classList.add('open');
    this.input?.focus();
  }

  public close(): void {
    this.isOpen = false;
    this.modal?.classList.remove('open');
  }

  private addMessage(role: 'user' | 'assistant', content: string, sources?: Source[]): void {
    const message: Message = {
      id: `msg-${Date.now()}`,
      role,
      content,
      timestamp: new Date(),
      sources,
    };

    this.messages.push(message);
    this.renderMessage(message);
  }

  private renderMessage(message: Message): void {
    if (!this.messagesContainer) return;

    const el = document.createElement('div');
    el.className = `librebot-message ${message.role}`;
    el.innerHTML = this.formatMessage(message.content);

    // Add sources if showSources is enabled and message has sources
    if (this.config.showSources && message.sources && message.sources.length > 0 && message.role === 'assistant') {
      const sourcesEl = this.renderSources(message.sources);
      el.appendChild(sourcesEl);
    }

    this.messagesContainer.appendChild(el);
    this.scrollToBottom();
  }

  private renderSources(sources: Source[]): HTMLElement {
    const container = document.createElement('div');
    container.className = 'librebot-sources';

    const label = document.createElement('span');
    label.className = 'librebot-sources-label';
    label.textContent = this.translations.sourcesLabel || 'Sources:';
    container.appendChild(label);

    sources.forEach(source => {
      const tag = document.createElement('span');
      tag.className = 'librebot-source-tag';
      const relevancePercent = Math.round(source.relevance * 100);
      tag.innerHTML = `${this.escapeHtml(source.documentName)} <span class="librebot-source-relevance">(${relevancePercent}%)</span>`;
      container.appendChild(tag);
    });

    return container;
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private formatMessage(content: string): string {
    // Escape HTML to prevent XSS
    const escapeHtml = (text: string): string => {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    // First, extract code blocks to protect them from other formatting
    // Regex handles optional space/newline after ``` and optional language identifier
    const codeBlocks: string[] = [];
    let processed = content.replace(/```\s*(\w*)\s*\n?([\s\S]*?)```/g, (_, lang, code) => {
      const escaped = escapeHtml(code.trim());
      const langClass = lang ? ` data-language="${escapeHtml(lang)}"` : '';
      codeBlocks.push(`<pre class="librebot-code-block"${langClass}><code>${escaped}</code></pre>`);
      return `%%CODEBLOCK${codeBlocks.length - 1}%%`;
    });

    // Extract inline code
    const inlineCodes: string[] = [];
    processed = processed.replace(/`([^`]+)`/g, (_, code) => {
      inlineCodes.push(`<code class="librebot-inline-code">${escapeHtml(code)}</code>`);
      return `%%INLINECODE${inlineCodes.length - 1}%%`;
    });

    // Now escape remaining HTML
    processed = escapeHtml(processed);

    // Headers (## and ###)
    processed = processed.replace(/^### (.+)$/gm, '<h4 class="librebot-h4">$1</h4>');
    processed = processed.replace(/^## (.+)$/gm, '<h3 class="librebot-h3">$1</h3>');
    processed = processed.replace(/^# (.+)$/gm, '<h2 class="librebot-h2">$1</h2>');

    // Bold and italic
    processed = processed.replace(/\*\*\*([^*]+)\*\*\*/g, '<strong><em>$1</em></strong>');
    processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    processed = processed.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    processed = processed.replace(/_([^_]+)_/g, '<em>$1</em>');

    // Links [text](url)
    processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="librebot-link">$1</a>');

    // Unordered lists
    processed = processed.replace(/^[\*\-] (.+)$/gm, '<li class="librebot-li">$1</li>');
    processed = processed.replace(/(<li class="librebot-li">.*<\/li>\n?)+/g, (match) => {
      return `<ul class="librebot-ul">${match}</ul>`;
    });

    // Ordered lists
    processed = processed.replace(/^\d+\. (.+)$/gm, '<li class="librebot-li-ordered">$1</li>');
    processed = processed.replace(/(<li class="librebot-li-ordered">.*<\/li>\n?)+/g, (match) => {
      return `<ol class="librebot-ol">${match}</ol>`;
    });

    // Horizontal rule
    processed = processed.replace(/^---$/gm, '<hr class="librebot-hr">');

    // Blockquotes
    processed = processed.replace(/^&gt; (.+)$/gm, '<blockquote class="librebot-blockquote">$1</blockquote>');

    // Convert newlines to <br> (but not inside lists/blockquotes)
    processed = processed.replace(/\n/g, '<br>');

    // Clean up extra <br> after block elements
    processed = processed.replace(/<\/(pre|ul|ol|blockquote|h[2-4])><br>/g, '</$1>');
    processed = processed.replace(/<br><(pre|ul|ol|blockquote|h[2-4])/g, '<$1');

    // Restore code blocks
    codeBlocks.forEach((block, i) => {
      processed = processed.replace(`%%CODEBLOCK${i}%%`, block);
    });

    // Restore inline code
    inlineCodes.forEach((code, i) => {
      processed = processed.replace(`%%INLINECODE${i}%%`, code);
    });

    return processed;
  }

  private showTyping(): HTMLDivElement {
    const typing = document.createElement('div');
    typing.className = 'librebot-typing';
    typing.innerHTML = '<span></span><span></span><span></span>';
    this.messagesContainer?.appendChild(typing);
    this.scrollToBottom();
    return typing;
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
    }
  }

  private async sendMessage(): Promise<void> {
    if (!this.input || this.isLoading) return;

    const content = this.input.value.trim();
    if (!content) return;

    // Add user message
    this.addMessage('user', content);
    this.input.value = '';

    // Show typing indicator
    this.isLoading = true;
    const typing = this.showTyping();

    try {
      if (this.config.streaming) {
        await this.callStreamingAPI(content, typing);
      } else {
        const response = await this.callAPI(content);
        typing.remove();

        if (response.error) {
          this.addMessage('assistant', `${this.translations.errorPrefix} ${response.error}`);
        } else {
          this.addMessage('assistant', response.response, response.sources);
          // Check for booking intent
          this.handleBookingIntent(response);
        }
      }
    } catch (error) {
      typing.remove();
      this.addMessage('assistant', this.translations.connectionError);
      console.error('LibreBot error:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async callStreamingAPI(message: string, typing: HTMLDivElement): Promise<void> {
    const response = await fetch(`${this.config.apiUrl}${this.getApiPath('chat')}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.config.apiKey}`,
      },
      body: JSON.stringify({
        message,
        context: this.getConversationContext(),
        stream: true,
        sessionId: this.sessionId,
      }),
    });

    if (!response.ok) {
      typing.remove();
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      this.addMessage('assistant', `${this.translations.errorPrefix} ${error.error || 'Request failed'}`);
      return;
    }

    // Remove typing indicator and create streaming message element
    typing.remove();
    const messageEl = document.createElement('div');
    messageEl.className = 'librebot-message assistant';
    this.messagesContainer?.appendChild(messageEl);

    const reader = response.body?.getReader();
    if (!reader) {
      this.addMessage('assistant', this.translations.streamingNotSupported);
      return;
    }

    const decoder = new TextDecoder();
    let fullContent = '';
    let bookingIntentData: ChatResponse | null = null;
    let receivedSources: Source[] = [];

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter((line) => line.trim() !== '');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullContent += parsed.content;
                messageEl.innerHTML = this.formatMessage(fullContent);
                this.scrollToBottom();
              }
              // Save sessionId when received from server
              if (parsed.sessionId) {
                this.saveSession(parsed.sessionId);
              }
              // Capture sources from metadata
              if (parsed.sources && Array.isArray(parsed.sources)) {
                receivedSources = parsed.sources;
              }
              // Check for booking intent in metadata
              if (parsed.booking_intent && parsed.booking_config) {
                bookingIntentData = parsed;
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      }
    } catch (error) {
      console.error('Stream reading error:', error);
    }

    // Add sources to message element if showSources is enabled
    if (this.config.showSources && receivedSources.length > 0) {
      const sourcesEl = this.renderSources(receivedSources);
      messageEl.appendChild(sourcesEl);
    }

    // Add to messages array
    this.messages.push({
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: fullContent,
      timestamp: new Date(),
      sources: receivedSources.length > 0 ? receivedSources : undefined,
    });

    // Handle booking intent if detected
    if (bookingIntentData) {
      this.handleBookingIntent(bookingIntentData);
    }
  }

  private async callAPI(message: string): Promise<ChatResponse> {
    const response = await fetch(`${this.config.apiUrl}${this.getApiPath('chat')}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.config.apiKey}`,
      },
      body: JSON.stringify({
        message,
        context: this.getConversationContext(),
        sessionId: this.sessionId,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      return { response: '', error: error.error || 'Request failed' };
    }

    const data = await response.json();
    // Save sessionId when received from server
    if (data.sessionId) {
      this.saveSession(data.sessionId);
    }
    return data;
  }

  private getConversationContext(): Array<{ role: string; content: string }> {
    // Get last 10 messages for context
    return this.messages.slice(-10).map((m) => ({
      role: m.role,
      content: m.content,
    }));
  }

  public destroy(): void {
    this.container?.remove();
    document.getElementById('librebot-styles')?.remove();
  }

  public clearSession(): void {
    try {
      this.sessionId = null;
      this.messages = [];
      localStorage.removeItem(this.getSessionKey());
      if (this.messagesContainer) {
        this.messagesContainer.innerHTML = '';
      }
      // Re-add welcome message
      if (this.config.welcomeMessage) {
        this.addMessage('assistant', this.config.welcomeMessage);
      }
    } catch {
      // localStorage may not be available
    }
  }

  // Booking Mode Methods

  private switchToBookingMode(config: BookingConfig): void {
    this.bookingConfig = config;
    this.mode = 'booking';
    this.selectedDate = null;
    this.selectedSlot = null;
    this.availableSlots = [];

    // Hide chat, show booking
    if (this.chatContainer) this.chatContainer.style.display = 'none';
    if (this.inputArea) this.inputArea.style.display = 'none';
    if (this.bookingContainer) {
      this.bookingContainer.style.display = 'flex';
      this.renderBookingUI();
    }
  }

  private switchToChatMode(): void {
    this.mode = 'chat';
    this.bookingConfig = null;
    this.selectedDate = null;
    this.selectedSlot = null;
    this.availableSlots = [];

    // Show chat, hide booking
    if (this.chatContainer) this.chatContainer.style.display = 'flex';
    if (this.inputArea) this.inputArea.style.display = 'flex';
    if (this.bookingContainer) {
      this.bookingContainer.style.display = 'none';
      this.bookingContainer.innerHTML = '';
    }
  }

  private renderBookingUI(): void {
    if (!this.bookingContainer || !this.bookingConfig) return;

    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 1);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + (this.bookingConfig.advance_booking_days || 30));

    const formFieldsHtml = this.bookingConfig.form_fields.map(field => {
      const requiredMark = field.required ? '<span class="required">*</span>' : '';
      const requiredAttr = field.required ? 'required' : '';

      if (field.type === 'textarea') {
        return `
          <div class="librebot-form-field">
            <label class="librebot-form-label">${field.label}${requiredMark}</label>
            <textarea
              class="librebot-form-textarea"
              name="${field.name}"
              placeholder="${field.placeholder || ''}"
              ${requiredAttr}
            ></textarea>
          </div>
        `;
      }

      if (field.type === 'select' && field.options) {
        const optionsHtml = field.options.map(opt =>
          `<option value="${opt}">${opt}</option>`
        ).join('');
        return `
          <div class="librebot-form-field">
            <label class="librebot-form-label">${field.label}${requiredMark}</label>
            <select
              class="librebot-form-select"
              name="${field.name}"
              ${requiredAttr}
            >
              <option value="">${field.placeholder || 'Select...'}</option>
              ${optionsHtml}
            </select>
          </div>
        `;
      }

      return `
        <div class="librebot-form-field">
          <label class="librebot-form-label">${field.label}${requiredMark}</label>
          <input
            type="${field.type}"
            class="librebot-form-input"
            name="${field.name}"
            placeholder="${field.placeholder || ''}"
            ${requiredAttr}
          />
        </div>
      `;
    }).join('');

    this.bookingContainer.innerHTML = `
      <div class="librebot-booking">
        <div class="librebot-booking-header">
          <button class="librebot-booking-back">${backIcon}</button>
          <h3 class="librebot-booking-title">${this.translations.bookingTitle}</h3>
        </div>
        <div class="librebot-booking-content">
          <div class="librebot-booking-section">
            <label class="librebot-booking-label">${this.translations.selectDate}</label>
            <input
              type="date"
              class="librebot-date-input"
              min="${minDate.toISOString().split('T')[0]}"
              max="${maxDate.toISOString().split('T')[0]}"
            />
          </div>
          <div class="librebot-booking-section">
            <label class="librebot-booking-label">${this.translations.selectTime}</label>
            <div class="librebot-slots-container">
              <div class="librebot-slots-empty">${this.translations.selectDate}</div>
            </div>
          </div>
          <div class="librebot-booking-section librebot-form-section" style="display: none;">
            <label class="librebot-booking-label">${this.translations.yourDetails}</label>
            <form class="librebot-booking-form">
              ${formFieldsHtml}
              <button type="submit" class="librebot-booking-submit" disabled>
                ${this.translations.confirmBooking}
              </button>
            </form>
          </div>
        </div>
      </div>
    `;

    // Add event listeners
    const backBtn = this.bookingContainer.querySelector('.librebot-booking-back');
    backBtn?.addEventListener('click', () => this.switchToChatMode());

    const dateInput = this.bookingContainer.querySelector('.librebot-date-input') as HTMLInputElement;
    dateInput?.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      this.selectedDate = target.value;
      this.selectedSlot = null;
      this.loadAvailableSlots(target.value);
    });

    const form = this.bookingContainer.querySelector('.librebot-booking-form') as HTMLFormElement;
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitBooking(form);
    });
  }

  private async loadAvailableSlots(date: string): Promise<void> {
    const slotsContainer = this.bookingContainer?.querySelector('.librebot-slots-container');
    if (!slotsContainer) return;

    slotsContainer.innerHTML = `<div class="librebot-slots-loading">${this.translations.loadingSlots}</div>`;

    try {
      const response = await fetch(
        `${this.config.apiUrl}/bookings/slots?date=${date}&timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
        {
          headers: {
            Authorization: `Bearer ${this.config.apiKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load slots');
      }

      const data = await response.json();
      this.availableSlots = data.slots || [];

      if (this.availableSlots.length === 0) {
        slotsContainer.innerHTML = `<div class="librebot-slots-empty">${this.translations.noSlotsAvailable}</div>`;
        return;
      }

      const slotsHtml = this.availableSlots.map(slot => {
        const disabled = !slot.available ? 'disabled' : '';
        return `
          <button
            type="button"
            class="librebot-slot"
            data-start="${slot.start}"
            data-end="${slot.end}"
            ${disabled}
          >${slot.start}</button>
        `;
      }).join('');

      slotsContainer.innerHTML = `<div class="librebot-slots">${slotsHtml}</div>`;

      // Add click handlers to slot buttons
      const slotButtons = slotsContainer.querySelectorAll('.librebot-slot');
      slotButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          const start = btn.getAttribute('data-start') || '';
          const end = btn.getAttribute('data-end') || '';
          this.selectSlot({ start, end, available: true }, btn);
        });
      });
    } catch (err) {
      console.error('Failed to load slots:', err);
      slotsContainer.innerHTML = `<div class="librebot-slots-empty">${this.translations.bookingError}</div>`;
    }
  }

  private selectSlot(slot: TimeSlot, buttonEl: Element): void {
    this.selectedSlot = slot;

    // Update button styles
    const allSlots = this.bookingContainer?.querySelectorAll('.librebot-slot');
    allSlots?.forEach(el => el.classList.remove('selected'));
    buttonEl.classList.add('selected');

    // Show form section
    const formSection = this.bookingContainer?.querySelector('.librebot-form-section') as HTMLElement;
    if (formSection) {
      formSection.style.display = 'block';
    }

    // Enable submit button
    const submitBtn = this.bookingContainer?.querySelector('.librebot-booking-submit') as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.disabled = false;
    }

    // Scroll to form
    formSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private async submitBooking(form: HTMLFormElement): Promise<void> {
    if (!this.selectedDate || !this.selectedSlot || !this.bookingConfig) return;

    const submitBtn = form.querySelector('.librebot-booking-submit') as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = '...';
    }

    // Collect form data
    const formData = new FormData(form);
    const formDataObj: Record<string, string> = {};
    formData.forEach((value, key) => {
      formDataObj[key] = value.toString();
    });

    try {
      const response = await fetch(`${this.config.apiUrl}/bookings/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.config.apiKey}`,
        },
        body: JSON.stringify({
          date: this.selectedDate,
          slot_start: this.selectedSlot.start,
          slot_end: this.selectedSlot.end,
          form_data: formDataObj,
          session_id: this.sessionId,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });

      const result: BookingResult = await response.json();

      if (result.success && result.booking) {
        this.renderBookingSuccess(result.booking);
      } else {
        throw new Error(result.error || 'Booking failed');
      }
    } catch (err) {
      console.error('Booking submission failed:', err);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = this.translations.confirmBooking;
      }
      alert(this.translations.bookingError);
    }
  }

  private renderBookingSuccess(booking: NonNullable<BookingResult['booking']>): void {
    if (!this.bookingContainer) return;

    // Format the date and time
    const startDate = new Date(booking.scheduled_start);
    const formattedDate = startDate.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const formattedTime = startDate.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });

    const meetingLinkHtml = booking.meeting_link
      ? `<a href="${booking.meeting_link}" target="_blank" class="librebot-meeting-link">${this.translations.joinMeeting}</a>`
      : '';

    this.bookingContainer.innerHTML = `
      <div class="librebot-booking">
        <div class="librebot-booking-header">
          <button class="librebot-booking-back">${backIcon}</button>
          <h3 class="librebot-booking-title">${this.translations.bookingTitle}</h3>
        </div>
        <div class="librebot-booking-content">
          <div class="librebot-booking-success">
            <div class="librebot-booking-success-icon">${checkIcon}</div>
            <h3>${this.translations.bookingSuccess}</h3>
            <div class="librebot-booking-details">
              <div class="librebot-booking-detail">
                <span class="librebot-booking-detail-label">${this.translations.selectDate}</span>
                <span class="librebot-booking-detail-value">${formattedDate}</span>
              </div>
              <div class="librebot-booking-detail">
                <span class="librebot-booking-detail-label">${this.translations.selectTime}</span>
                <span class="librebot-booking-detail-value">${formattedTime}</span>
              </div>
              <div class="librebot-booking-detail">
                <span class="librebot-booking-detail-label">${this.translations.duration}</span>
                <span class="librebot-booking-detail-value">${booking.duration_minutes} ${this.translations.minutes}</span>
              </div>
            </div>
            ${meetingLinkHtml}
            <button class="librebot-booking-submit" type="button">${this.translations.backToChat}</button>
          </div>
        </div>
      </div>
    `;

    // Add event listeners
    const backBtn = this.bookingContainer.querySelector('.librebot-booking-back');
    backBtn?.addEventListener('click', () => this.switchToChatMode());

    const chatBtn = this.bookingContainer.querySelector('.librebot-booking-submit');
    chatBtn?.addEventListener('click', () => this.switchToChatMode());
  }

  private handleBookingIntent(data: ChatResponse): void {
    if (data.booking_intent && data.booking_config) {
      // Add a brief delay so user can see the AI response before switching
      setTimeout(() => {
        this.switchToBookingMode(data.booking_config!);
      }, 1500);
    }
  }
}
